import { useCallback, useMemo, useState, type Key } from 'react';
import { Alert, Button, Input, Modal, Select, Space, Typography } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { IncidentListItem } from '@/app/services/incident/incident.service';
import { createIncidentTableBodyRow } from './createIncidentTableBodyRow';
import {
  useIncidentTableColumns,
  getIncidentOmpRowClassName,
} from './hooks/useIncidentTableColumns';
import { IncidentOmpFormulaNote } from './IncidentOmpFormulaNote';
import { BlockingReason, IncidentStatus } from '@evimo/types/index';
import { INCIDENT_BLOCKING_REASONS } from '@/app/hooks/incident/useIncidentStatusTransition';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import {
  FilterBarActions,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';

const { Paragraph, Text, Title } = Typography;

type IncidentOverviewProps = {
  blockingReason: BlockingReason;
  incidents: IncidentListItem[];
  errorMessage: string | null;
  isFetching: boolean;
  isLoading: boolean;
  isTransitionDialogOpen: boolean;
  isTransitioning: boolean;
  pendingTransition: { incidentId: string; nextStatus: IncidentStatus } | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger: boolean;
  };
  searchValue: string;
  onBlockingReasonChangeAction: (nextValue: BlockingReason) => void;
  onCancelTransitionAction: () => void;
  onConfirmTransitionAction: () => void;
  onIncidentSelectAction: (incidentId: string) => void;
  onRefreshAction: () => void;
  onSearchChangeAction: (nextValue: string) => void;
  onTableChangeAction: (nextPagination: TablePaginationConfig) => void;
  onTransitionAction: (incidentId: string, nextStatus: IncidentStatus) => void;
  transitioningIncidentId: string | null;
};

export function IncidentOverview({
  blockingReason,
  incidents,
  errorMessage,
  isFetching,
  isLoading,
  isTransitionDialogOpen,
  isTransitioning,
  pendingTransition,
  pagination,
  searchValue,
  onBlockingReasonChangeAction,
  onCancelTransitionAction,
  onConfirmTransitionAction,
  onIncidentSelectAction,
  onRefreshAction,
  onSearchChangeAction,
  onTableChangeAction,
}: IncidentOverviewProps) {
  const { t } = useTranslation('incidents');
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const onExpandedRowsChangeAction = useCallback((keys: readonly Key[]) => {
    setExpandedRowKeys(keys.map(String));
  }, []);
  const { columns, expandable } = useIncidentTableColumns({
    expandedRowKeys,
    onExpandedRowsChangeAction,
    onOpenDetailsAction: onIncidentSelectAction,
  });
  const tableComponents = useMemo(
    () => ({
      body: {
        row: createIncidentTableBodyRow(
          incidents,
          columns.length + 1,
          expandedRowKeys,
        ),
      },
    }),
    [columns.length, expandedRowKeys, incidents],
  );

  return (
    <Space orientation="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('overview.title', 'Incidents')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'overview.description',
            'Browse the incidents currently detected across the active organization workspace.',
          )}
        </Paragraph>
        <IncidentOmpFormulaNote />
      </div>

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message={t('overview.loadFailed', 'Failed to load incidents')}
          description={errorMessage}
        />
      ) : null}

      <EvimoListPanel>
        <FilterBar>
          <Input
            allowClear
            className="evimo-filter-search"
            placeholder={t(
              'overview.searchPlaceholder',
              'Search by incident, station or summary',
            )}
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(event) => onSearchChangeAction(event.target.value)}
          />

          <FilterBarActions>
            <FilterBarRefreshButton
              ns="incidents"
              labelKey="overview.refresh"
              loading={isFetching}
              onClick={onRefreshAction}
            />
          </FilterBarActions>
        </FilterBar>

        <div className="incident-overview-table-scroll">
          <EvimoTable<IncidentListItem>
            className="ds-table incident-overview-table"
            columns={columns}
            dataSource={incidents}
            loading={isLoading}
            rowKey="id"
            pagination={pagination}
            size="small"
            tableLayout="fixed"
            expandable={expandable}
            components={tableComponents}
            onChange={onTableChangeAction}
            onRow={(record) => ({
              className: getIncidentOmpRowClassName(record),
            })}
            locale={{
              emptyText: t(
                'overview.empty',
                'No incidents found for this organization.',
              ),
            }}
          />
        </div>
      </EvimoListPanel>

      <Modal
        open={isTransitionDialogOpen}
        title={t('actions.blockedModal.title', 'Set blocking reason')}
        onCancel={onCancelTransitionAction}
        footer={[
          <Button
            key="cancel"
            onClick={onCancelTransitionAction}
            disabled={isTransitioning}
          >
            {t('actions.cancel', 'Cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isTransitioning}
            onClick={onConfirmTransitionAction}
          >
            {t('actions.confirm', 'Confirm')}
          </Button>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            {t(
              'actions.blockedModal.description',
              'Choose the reason that blocks this incident before moving it to Blocked.',
            )}
          </Text>
          <Select<BlockingReason>
            value={blockingReason}
            options={INCIDENT_BLOCKING_REASONS.map((reason) => ({
              label: t(`blockingReason.${reason}`, reason),
              value: reason,
            }))}
            onChange={onBlockingReasonChangeAction}
          />
          {pendingTransition ? (
            <Text type="secondary">
              {t('actions.blockedModal.targetStatus', {
                status: t(
                  `status.${pendingTransition.nextStatus}`,
                  pendingTransition.nextStatus,
                ),
                defaultValue: 'Next status: {{status}}',
              })}
            </Text>
          ) : null}
        </Space>
      </Modal>
    </Space>
  );
}
